"use client";

import React, { createContext, useContext, useEffect, useState } from "react";
import { useUser } from "@clerk/nextjs";
import { usePathname, useRouter } from "next/navigation";
import { db } from "@/lib/db";
import { users } from "@/lib/db/schema";
import { eq } from "drizzle-orm";

interface UserStatus {
  id: string;
  suspended: boolean;
  suspendedAt: Date | string | null;
  suspensionReason: string | null;
  role: string;
}

interface UserProtectionContextType {
  userStatus: UserStatus | null;
  isLoading: boolean;
  checkUserStatus: () => Promise<void>;
}

const UserProtectionContext = createContext<UserProtectionContextType>({
  userStatus: null,
  isLoading: true,
  checkUserStatus: async () => {},
});

export const useUserProtection = () => {
  const context = useContext(UserProtectionContext);
  if (!context) {
    throw new Error(
      "useUserProtection must be used within a UserProtectionProvider",
    );
  }
  return context;
};

const isUserRoute = (pathname: string): boolean => {
  const userRoutes = [
    "/dashboard",
    "/user",
    "/deposit",
    "/withdraw",
    "/history",
    "/profile",
    "/settings",
  ];
  return userRoutes.some((route) => pathname.startsWith(route));
};

const isAdminRoute = (pathname: string): boolean => {
  const adminRoutes = ["/adminDashboard", "/admin"];
  return adminRoutes.some((route) => pathname.startsWith(route));
};

const isPublicRoute = (pathname: string): boolean => {
  const publicRoutes = [
    "/",
    "/sign-in",
    "/sign-up",
    "/trade",
    "/features",
    "/invest",
    "/about",
    "/contact",
    "/sync-user",
    "/suspended",
    "/blocked",
  ];
  return publicRoutes.some((route) => {
    if (route === "/" && pathname === "/") return true;
    if (route !== "/" && pathname.startsWith(route)) return true;
    return false;
  });
};

// Utility function to safely handle date conversion
const safeDateToISOString = (date: Date | string | null): string => {
  if (!date) return "";
  if (typeof date === "string") return date;
  try {
    return new Date(date).toISOString();
  } catch (error) {
    console.warn("Invalid date format:", date);
    return "";
  }
};

async function checkUserSuspension(userId: string): Promise<UserStatus | null> {
  try {
    const response = await fetch("/api/user/status", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ userId }),
    });

    if (!response.ok) {
      throw new Error("Failed to fetch user status");
    }

    const userStatus = await response.json();
    return userStatus;
  } catch (error) {
    console.error("Error checking user suspension:", error);
    return null;
  }
}

interface UserProtectionProviderProps {
  children: React.ReactNode;
}

export const UserProtectionProvider: React.FC<UserProtectionProviderProps> = ({
  children,
}) => {
  const { user, isLoaded } = useUser();
  const router = useRouter();
  const pathname = usePathname();
  const [userStatus, setUserStatus] = useState<UserStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const checkUserStatus = async () => {
    if (!user?.id || isPublicRoute(pathname)) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const status = await checkUserSuspension(user.id);
      setUserStatus(status);

      // Handle user suspension for user routes
      if (isUserRoute(pathname) && status) {
        if (status.suspended) {
          const suspensionUrl = new URLSearchParams({
            reason: status.suspensionReason || "Account suspended",
            date: safeDateToISOString(status.suspendedAt),
          });
          router.replace(`/suspended?${suspensionUrl}`);
          return;
        }

        // If user doesn't exist in database, redirect to sync
        if (!status) {
          router.replace("/sync-user");
          return;
        }
      }

      // Handle admin route access
      // Note: Admin route unauthorized access is handled by the layout component
      // which shows an Unauthorized component and signs out the user
      if (isAdminRoute(pathname) && status) {
        // Don't allow suspended admins
        if (status.suspended) {
          const suspensionUrl = new URLSearchParams({
            reason: status.suspensionReason || "Account suspended",
            date: safeDateToISOString(status.suspendedAt),
          });
          router.replace(`/suspended?${suspensionUrl}`);
          return;
        }
        // Role check is handled in the admin layout component
      }
    } catch (error) {
      console.error("Error in user protection:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isLoaded) {
      checkUserStatus();
    }
  }, [isLoaded, user?.id, pathname]);

  return (
    <UserProtectionContext.Provider
      value={{
        userStatus,
        isLoading,
        checkUserStatus,
      }}
    >
      {children}
    </UserProtectionContext.Provider>
  );
};
